import classnames from 'classnames';
import React, { FC, useCallback, useEffect } from 'react';
import { NativeTypes } from 'react-dnd-html5-backend';
import { FormattedMessage } from 'react-intl';
import { MemoryRouter } from 'react-router';
import { DropZone, Heading, Text } from '../../components/common';
import { BiAddMediaActionTarget, BiAddMediaClickOrigin } from '../../constants/Bi';
import { ModalName } from '../../constants/ModalName';
import { ExternalSource, MainSource } from '../../constants/Source';
import { useTrackAddMediaMediaSourceSelected } from '../../utils/hooks/bi/useTrackAddMediaMediaSourceSelected';
import { useEffectButSkipMount } from '../../utils/hooks/useEffectButSkipMount';
import { useMediaManagerSDK } from '../../utils/hooks/useMediaManagerSDK';
import { useModalHandlersAndOptions } from '../../utils/hooks/useModal';
import { Content } from './components/Content';
import { Sources } from './components/Sources';
import styles from './FilesImporter.scss';
import { IFilesImporterOuter } from './FilesImporter.types';
import { useSourceState } from './hooks/useSourceState';

const accepts = [NativeTypes.FILE];
export const FilesImporter: FC<IFilesImporterOuter> = ({ onSelect, contentClassName, folderPath }) => {
  const { trackAddMediaMediaSourceSelectedImportFilesTabs } = useTrackAddMediaMediaSourceSelected();

  const { createNewFolderMP } = useMediaManagerSDK({ source: MainSource.SiteFiles });

  const {
    additionalOptions: { openedFrom },
    setCloseParams,
  } = useModalHandlersAndOptions(ModalName.FilesImporter);

  const {
    connectorsMap,
    selectedSource,
    connectedSources,
    permanentSources,
    isSelectedSourceConnected,
    connect,
    disconnect,
    changeSource,
  } = useSourceState();

  useEffect(() => {
    setCloseParams({ selectedSource });
  }, [setCloseParams, selectedSource]);

  useEffectButSkipMount(() => {
    trackAddMediaMediaSourceSelectedImportFilesTabs(selectedSource);
  }, [selectedSource]);

  const handleFilesDragAndDrop = useCallback((files, _folder, options) => onSelect(files, options), [onSelect]);

  return (
    <MemoryRouter>
      <div className={styles.wrapper}>
        <Sources
          permanentSources={permanentSources}
          connectedSources={connectedSources}
          selectedSource={selectedSource}
          onSourceChange={changeSource}
          onDisconnectClick={disconnect}
        />

        <Content
          openedFrom={openedFrom}
          source={selectedSource}
          isConnected={isSelectedSourceConnected}
          onConnectClicked={connect}
          onSourceSessionExpired={disconnect}
          onSelect={onSelect}
          className={contentClassName}
          connectorsMap={connectorsMap}
          folderPath={folderPath}
        />

        <DropZone
          className={classnames(styles.dropZone, {
            [styles.noContent]: selectedSource === ExternalSource.Computer,
          })}
          activeClassName={styles.dropZoneActive}
          draggingClassName={styles.dropZoneDragging}
          accepts={accepts}
          onDrop={handleFilesDragAndDrop}
          biOrigin={BiAddMediaClickOrigin.ImportFilesTabs}
          biTarget={BiAddMediaActionTarget.UploadDragAndDrop}
          createFolderFn={createNewFolderMP}
        >
          <div className={styles.dropZoneContentWrapper}>
            <div className={styles.dropZoneContent}>
              <Heading className={styles.dropZoneTitle} size="medium">
                <FormattedMessage id="files-importer.drop-zone.title" />
              </Heading>
              <Text className={styles.dropZoneSubTitle} weight="thin" size="medium" secondary>
                <FormattedMessage id="files-importer.drop-zone.subtitle" />
              </Text>
            </div>
          </div>

          <div className={styles.dropZoneOverlay} />
        </DropZone>
      </div>
    </MemoryRouter>
  );
};
